import { create } from 'zustand';
import { AppOperator, Theme } from '../types/theme.types';
import { getSettings, updateSettings } from '../utils/local-storage';

type ThemeStore = {
  theme: Theme;
  isDarkMode: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  operator: AppOperator;
  setOperator: (operator: AppOperator) => void;
  mocks: boolean;
  toggleMocks: () => void;
};

export const getInitialThemeState = () => {
  let isDarkMode: boolean = false;
  let mocks: boolean = false;
  let operator: AppOperator = AppOperator.DEFAULT;

  if (typeof window !== 'undefined') {
    const savedTheme = getSettings()?.theme;

    if (savedTheme) {
      isDarkMode = savedTheme === Theme.DARK;
    } else {
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    const savedOperator = getSettings()?.operator;
    if (savedOperator) operator = savedOperator;

    const savedMocks = getSettings()?.mocks;
    if (savedMocks !== undefined) mocks = savedMocks;
  }

  return {
    theme: isDarkMode ? Theme.DARK : Theme.LIGHT,
    isDarkMode,
    operator,
    mocks,
  };
};

export const useThemeStore = create<ThemeStore>()((set) => {
  const { theme, isDarkMode, operator, mocks } = getInitialThemeState();

  return {
    theme,
    isDarkMode,
    operator,
    mocks,
    toggleMocks: () => {
      set((state) => {
        updateSettings({ mocks: !state.mocks });
        return { mocks: !state.mocks };
      });
    },
    setTheme: (theme: Theme) =>
      set(() => {
        updateSettings({ theme });
        return { theme, isDarkMode: theme === Theme.DARK };
      }),
    toggleTheme: () =>
      set((state) => {
        const newTheme = state.isDarkMode ? Theme.LIGHT : Theme.DARK;
        updateSettings({ theme: newTheme });

        return { theme: newTheme, isDarkMode: newTheme === Theme.DARK };
      }),
    setOperator: (operator: AppOperator) =>
      set(() => {
        updateSettings({ operator });
        return { operator };
      }),
  };
});
